import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';

import ThreeWisemenCouncil from '../components/ThreeWisemenCouncil';
import {
  AskQuota,
  getErrorCode,
  getUserErrorMessage,
  ragAPI,
  RAGCitation,
} from '../api/api';
import {
  wisemenAPI,
  WisemanAgentId,
  WisemenTurn,
} from '../api/wisemen';
import { suggestWiseman, wisemanById, WisemanId } from '../wisemen/personas';
import '../styles/ask.css';


function responseStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined;
  }
  const response = (error as { response?: { status?: unknown } }).response;
  return typeof response?.status === 'number' ? response.status : undefined;
}

function CitationList({ citations }: { citations: RAGCitation[] }) {
  if (citations.length === 0) {
    return null;
  }
  return (
    <div className="ask-citations">
      <h3>Sources used</h3>
      <ol>
        {citations.map((citation) => {
          const pageEnd = citation.page_end ?? citation.page_start;
          const pageLabel =
            citation.page_start != null
              ? ` · page${citation.page_start === pageEnd ? '' : 's'} ${citation.page_start}${
                  pageEnd !== citation.page_start ? `–${pageEnd}` : ''
                }`
              : '';
          return (
            <li key={`${citation.type}-${citation.id}`}>
              <strong>{citation.title}</strong>
              <span>
                {citation.source}
                {pageLabel}
              </span>
              {citation.source_url && (
                <a href={citation.source_url} target="_blank" rel="noopener noreferrer">
                  Open source
                </a>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function TurnBubble({ turn }: { turn: WisemenTurn }) {
  if (turn.role === 'user') {
    return (
      <div className="ask-turn ask-turn--user">
        <p className="ask-turn__label">You</p>
        <div className="ask-turn__body">{turn.content}</div>
      </div>
    );
  }

  const persona = turn.agent_id ? wisemanById(turn.agent_id) : undefined;
  const accent = persona?.accent ?? 'var(--neon-magenta)';
  const name = persona?.name ?? 'Wiseman';

  return (
    <article
      className="ask-turn ask-turn--assistant"
      style={{ '--wiseman-accent': accent } as React.CSSProperties}
    >
      <div className="ask-turn__header">
        <div className="ask-result__lead-avatar" aria-hidden>
          {name.charAt(0)}
        </div>
        <p className="ask-turn__label">{name}</p>
      </div>
      <div className="ask-turn__body ask-answer">{turn.content}</div>
      <CitationList citations={turn.citations} />
    </article>
  );
}

const Ask: React.FC = () => {
  const { conversationId: routeConversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();

  const [conversationId, setConversationId] = useState<string | undefined>(routeConversationId);
  const [turns, setTurns] = useState<WisemenTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [quota, setQuota] = useState<AskQuota | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<WisemanId | undefined>();
  const [respondingAgent, setRespondingAgent] = useState<WisemanId | undefined>();

  useEffect(() => {
    let cancelled = false;
    ragAPI
      .quota()
      .then((status) => {
        if (!cancelled) {
          setQuota(status);
        }
      })
      .catch(() => {
        // Backend still enforces the cap if this preview fails.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadConversation = useCallback(async (id: string) => {
    setLoadingThread(true);
    setError('');
    try {
      const detail = await wisemenAPI.getConversation(id);
      setConversationId(detail.id);
      setTurns(detail.turns);
    } catch {
      setError('That conversation could not be loaded.');
      setConversationId(undefined);
      setTurns([]);
      navigate('/ask', { replace: true });
    } finally {
      setLoadingThread(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (routeConversationId) {
      void loadConversation(routeConversationId);
      return;
    }
    setConversationId(undefined);
    setTurns([]);
  }, [routeConversationId, loadConversation]);

  const councilPhase = loading ? 'consulting' : turns.length > 0 ? 'answered' : 'idle';
  const leadId = respondingAgent ?? selectedAgent ?? (draft ? suggestWiseman(draft).id : undefined);

  const handleNewConversation = () => {
    setTurns([]);
    setConversationId(undefined);
    setDraft('');
    setError('');
    setSelectedAgent(undefined);
    setRespondingAgent(undefined);
    navigate('/ask');
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const message = draft.trim();
    if (message.length < 1 || loading) {
      return;
    }

    setLoading(true);
    setError('');
    setRespondingAgent(selectedAgent ?? suggestWiseman(message).id);

    let activeId = conversationId;
    try {
      if (!activeId) {
        const created = await wisemenAPI.newConversation();
        activeId = created.conversation_id;
        setConversationId(activeId);
        navigate(`/ask/${activeId}`, { replace: true });
      }

      const response = await wisemenAPI.chat(
        activeId,
        message,
        (selectedAgent ?? undefined) as WisemanAgentId | undefined
      );

      const detail = await wisemenAPI.getConversation(activeId);
      setTurns(detail.turns);
      setDraft('');
      setRespondingAgent(response.agent.id);
      if (response.quota) {
        setQuota(response.quota);
      }
    } catch (err: unknown) {
      setRespondingAgent(undefined);
      const status = responseStatus(err);
      let code: string | undefined;
      try {
        code = getErrorCode(err);
      } catch {
        code = undefined;
      }

      let messageText = 'The council could not reach the library just now. Please try again.';
      if (code === 'quota_exceeded' || status === 429) {
        messageText = "You have reached today's council question limit. It resets at midnight UTC.";
      } else if (code === 'question_too_large') {
        messageText = 'That question needs too much research context. Try a more focused question.';
      } else if (code === 'email_not_verified') {
        messageText = 'Verify your email to consult the Wisemen. Check your inbox for a verification link.';
      }
      try {
        messageText = getUserErrorMessage(err, messageText);
      } catch {
        // Keep the fallback if error mapping throws in tests.
      }
      setError(messageText);
    } finally {
      setLoading(false);
    }
  };

  const quotaLabel = quota?.unlimited
    ? 'Admin access — the council has no daily limit'
    : quota
      ? `${quota.questions_remaining ?? 0} of ${quota.questions_limit ?? 4} council questions left today`
      : null;
  const quotaExhausted = Boolean(
    quota && !quota.unlimited && (quota.questions_remaining ?? 0) <= 0
  );

  return (
    <motion.main
      className="container ask-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <section className="card ask-intro">
        <p className="ask-eyebrow">The Three Wisemen · Wisemen Research</p>
        <h1>Ask the council</h1>
        <p>
          Sasha, Pickle Rick, and Dr. Gonzo are three separate agents — each with their own
          voice, grounded in the curated library. Conversations persist so you can follow up.
          Citations appear with supported claims. Free members may ask up to four questions per
          day; subscribers get 100× that daily capacity.
        </p>
        {quotaLabel && <p className="ask-quota">{quotaLabel}</p>}
        {conversationId && (
          <p className="ask-thread-actions">
            <button type="button" className="btn btn-secondary ask-new-thread" onClick={handleNewConversation}>
              New conversation
            </button>
          </p>
        )}
      </section>

      <ThreeWisemenCouncil
        phase={councilPhase}
        leadId={leadId}
        selectedId={selectedAgent}
        onSelectAgent={setSelectedAgent}
      />

      {turns.length > 0 && (
        <section className="card ask-thread" aria-label="Conversation history">
          <h2>Conversation</h2>
          {loadingThread ? (
            <p className="ask-thread-loading">Loading conversation…</p>
          ) : (
            <div className="ask-thread__messages">
              {turns.map((turn, index) => (
                <TurnBubble key={`${turn.id}-${index}`} turn={turn} />
              ))}
            </div>
          )}
        </section>
      )}

      <section className="card ask-panel" aria-labelledby="ask-form-title">
        <h2 id="ask-form-title">Your message</h2>
        <form onSubmit={handleSubmit}>
          <label className="form-label" htmlFor="research-question">
            What would you like the Wisemen to explore?
          </label>
          <textarea
            id="research-question"
            className="form-input ask-textarea"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="For example: What does the research say about psilocybin safety considerations?"
            minLength={1}
            maxLength={2000}
            required
            disabled={loading || quotaExhausted || loadingThread}
          />
          <p className="ask-routing-hint">
            {selectedAgent
              ? `Routing to ${wisemanById(selectedAgent)?.name ?? 'agent'} (your pick).`
              : draft.trim()
                ? `Suggested voice: ${suggestWiseman(draft).name} — tap a card above to override.`
                : 'Pick a Wiseman above or let the council route from your question.'}
          </p>
          <div className="ask-form-footer">
            <span>{draft.length}/2000</span>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={loading || quotaExhausted || loadingThread || draft.trim().length < 1}
            >
              {loading ? 'Council is consulting…' : turns.length ? 'Send follow-up' : 'Ask the Wisemen'}
            </button>
          </div>
        </form>

        {error && (
          <div className="error ask-error" role="alert">
            {error}
          </div>
        )}
      </section>

      <p className="ask-disclaimer ask-disclaimer--page">
        Educational information only. The Wisemen are not clinicians or your lawyers — this is
        not medical or legal advice and is not a substitute for care from a qualified
        professional.
      </p>
    </motion.main>
  );
};

export default Ask;
